import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PHRASES,
  type ParamSpec,
  type PhraseRef,
  type PingKind,
  type PuzzleType,
} from '@il-colpo/engine';
import { Button, Panel } from '../../ui/index.ts';
import { useGame } from '../GameContext.tsx';
import { usePadStore } from '../padStore.ts';
import { useLooseT } from '../text/loose.ts';
import styles from './PhrasePad.module.css';

const GENERAL = ['ready', 'wait', 'go', 'danger', 'needRole', 'done', 'repeat', 'dontKnow'];
const BY_PUZZLE: Record<PuzzleType, string[]> = {
  lock: ['lockNextPin', 'lockPinHeight'],
  safe: ['safeDigit', 'safeClue', 'safeNo', 'safeSum', 'safeParity', 'safeGreater', 'safeAttempt'],
  panel: ['panelSerial', 'panelWire', 'panelRule', 'panelRuleLetter'],
  cameras: ['camAvoid', 'camNow'],
  laser: ['laserMirror', 'laserEnds'],
  password: ['passClue', 'passTry'],
  corridor: ['corGuard', 'corHold', 'corShadow'],
  escape: ['escReinforce', 'escMove'],
  vault: ['vaultPos', 'vaultConfirm'],
};
const RADIO = ['bulletin'];
const HIDEOUT = ['suspect', 'trust', 'sawWeird'];
const SEAT_PHRASES = new Set(['corHold', 'suspect', 'trust']);
const PINGS: readonly PingKind[] = ['here', 'danger', 'wait', 'go'];

type Specs = readonly ParamSpec[];
const specsOf = (id: string): Specs =>
  (PHRASES as Record<string, { params: Specs }>)[id]?.params ?? [];

function defaults(id: string): (number | string)[] {
  return specsOf(id).map((s) => (s.kind === 'int' ? s.min : (s.values[0] as string)));
}

/** The quick phrases (GDD §13.1): pick one, fill in its parameters, say it. Plus the pings. */
export function PhrasePad() {
  const { t } = useTranslation('game');
  const { view, say, sentence, send, g } = useGame();
  const pl = useLooseT('phrases');
  const open = usePadStore((s) => s.open);
  const toggleOpen = usePadStore((s) => s.toggle);
  const [chosen, setChosen] = useState<string | null>(null);
  const [values, setValues] = useState<(number | string)[]>([]);

  const ids = useMemo(() => {
    const list = [...GENERAL];
    const types = new Set((view.exec?.puzzles ?? []).filter((x) => !x.solved).map((x) => x.type));
    for (const [type, group] of Object.entries(BY_PUZZLE) as [PuzzleType, string[]][]) {
      if (types.has(type)) list.push(...group);
    }
    if (view.game.phase === 'execution' || view.game.phase === 'escape') {
      if (view.you.role === 'driver') list.push(...RADIO);
    }
    if (view.hideout || view.result) list.push(...HIDEOUT);
    return list;
  }, [view.exec?.puzzles, view.game.phase, view.hideout, view.result, view.you.role]);

  const pick = (id: string) => {
    if (specsOf(id).length === 0) {
      say({ id, params: [] });
      return;
    }
    setChosen(id);
    setValues(defaults(id));
  };

  const phrase: PhraseRef | null = chosen ? { id: chosen, params: values } : null;

  return (
    <Panel tone="night" className={styles['pad']}>
      <Button
        variant="secondary"
        icon="chat"
        selected={open}
        onClick={toggleOpen}
        aria-expanded={open}
        block
      >
        {t('phrases.open')}
      </Button>
      {open ? (
        <div className={styles['body']}>
          <div className={styles['group']} role="group" aria-label={t('phrases.pings')}>
            {PINGS.map((kind) => (
              <Button
                key={kind}
                size="md"
                variant="ghost"
                onClick={() => send({ type: 'ping', kind, room: view.exec?.room ?? null })}
              >
                {g(`ping.${kind}`)}
              </Button>
            ))}
          </div>
          <ul className={styles['list']}>
            {ids.map((id) => (
              <li key={id}>
                <Button variant="ghost" selected={chosen === id} onClick={() => pick(id)}>
                  {pl(`names.${id}`)}
                </Button>
              </li>
            ))}
          </ul>
          {chosen && phrase ? (
            <div className={styles['editor']}>
              {specsOf(chosen).map((spec, i) => (
                <ParamField
                  key={`${chosen}-${i}`}
                  id={chosen}
                  index={i}
                  spec={spec}
                  value={values[i] as number | string}
                  onChange={(v) => setValues((cur) => cur.map((x, j) => (j === i ? v : x)))}
                />
              ))}
              <p className={styles['preview']}>{sentence(phrase)}</p>
              <Button
                icon="chat"
                onClick={() => {
                  if (say(phrase)) setChosen(null);
                }}
              >
                {t('phrases.say')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

function ParamField({
  id,
  index,
  spec,
  value,
  onChange,
}: {
  id: string;
  index: number;
  spec: ParamSpec;
  value: number | string;
  onChange: (v: number | string) => void;
}) {
  const { view } = useGame();
  const { t } = useTranslation('game');
  const pl = useLooseT('phrases');
  const label = t('phrases.param', { n: index + 1 });
  if (spec.kind === 'enum') {
    return (
      <select
        aria-label={label}
        className={styles['select']}
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
      >
        {spec.values.map((v) => (
          <option key={v} value={v}>
            {pl(`enum.${v}`)}
          </option>
        ))}
      </select>
    );
  }
  if (SEAT_PHRASES.has(id)) {
    return (
      <select
        aria-label={label}
        className={styles['select']}
        value={String(value)}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {view.players.map((p) => (
          <option key={p.id} value={p.seat + 1}>
            {p.name}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      aria-label={label}
      className={styles['number']}
      type="number"
      inputMode="numeric"
      min={spec.min}
      max={spec.max}
      value={Number(value)}
      onChange={(e) => {
        const n = Math.round(Number(e.target.value));
        onChange(Math.min(spec.max, Math.max(spec.min, Number.isFinite(n) ? n : spec.min)));
      }}
    />
  );
}
